import React, {Component} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import ReactDOM from 'react-dom';
import {connect} from 'react-redux';
import {setReportDateRangeFilter} from '../../../../../../../../../../services/redux/actions';
import {FormattedMessage, injectIntl} from 'react-intl';
import moment from 'moment';
import RaisedButton from 'material-ui/RaisedButton';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import Popover from 'material-ui/Popover';
import DayPickerContainer from './components/DayPickerContainer';
import NavigationArrowForward from 'material-ui/svg-icons/navigation/arrow-forward';
import {grey400, grey500} from 'material-ui/styles/colors';
import withQueryStringProps from '../../../../../../../../../../components/withQueryStringProps';
import HideIfPrint from '../../../../../../../../../../components/HideIfPrint';
import ShowIfPrint from '../../../../../../../../../../components/ShowIfPrint';

function getRangeTypes(intl, firstDate) {
  return [{
    id: 'last-week',
    label: intl.formatMessage({id: 'lastWeek'}),
    dateRange: {
      from: moment().isoWeekday(-6).format('YYYY-MM-DD'),
      to: moment().isoWeekday(1).format('YYYY-MM-DD'),
    },
  },{
    id: 'this-week',
    label: intl.formatMessage({id: 'thisWeek'}),
    dateRange: {
      from: moment().isoWeekday(1).format('YYYY-MM-DD'),
      to: moment().add(1, 'day').format('YYYY-MM-DD'),
    },
  },{
    id: 'last-month',
    label: intl.formatMessage({id: 'lastMonth'}),
    dateRange: {
      from: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      to: moment().subtract(1, 'month').endOf('month').add(1, 'day').format('YYYY-MM-DD'),
    },
  },{
    id: 'this-month',
    label: intl.formatMessage({id: 'thisMonth'}),
    dateRange: {
      from: moment().startOf('month').format('YYYY-MM-DD'),
      to: moment().add(1, 'day').format('YYYY-MM-DD'),
    },
  },{
    id: 'last-90-days',
    label: intl.formatMessage({id: 'last90Days'}),
    dateRange: {
      from: moment().subtract(90, 'day').format('YYYY-MM-DD'),
      to: moment().add(1, 'day').format('YYYY-MM-DD'),
    },
  },{
    id: 'last-year',
    label: intl.formatMessage({id: 'lastYear'}),
    dateRange: {
      from: moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
      to: moment().subtract(1, 'year').endOf('year').add(1, 'day').format('YYYY-MM-DD'),
    },
  },{
    id: 'this-year',
    label: intl.formatMessage({id: 'thisYear'}),
    dateRange: {
      from: moment().startOf('year').format('YYYY-MM-DD'),
      to: moment().add(1, 'day').format('YYYY-MM-DD'),
    },
  },{
    id: 'all-time',
    label: intl.formatMessage({id: 'allTime'}),
    dateRange: {
      from: firstDate || moment().format('YYYY-MM-DD'),
      to: moment().add(1, 'day').format('YYYY-MM-DD'),
    },
  },{
    id: 'custom',
    label: intl.formatMessage({id: 'custom'}),
  }];
}

function addOneDay(dateString) {
  return moment.utc(dateString).add(1, 'days').format('YYYY-MM-DD');
}

function subtractOneDay(dateString) {
  return moment.utc(dateString).subtract(1, 'days').format('YYYY-MM-DD');
}

function getStateFromProps(props) {
  const defaultDateRangeType = 'last-90-days';
  const rangeTypes = getRangeTypes(props.intl, props.firstDate);

  if (props.from && props.to) {
    const rt = rangeTypes.find(({dateRange = {}}) => {
      return dateRange.from === props.from && dateRange.to === addOneDay(props.to);
    });
    const rangeType = rt ? rt.id : 'custom';
    return {
      open: false,
      rangeType,
      from: props.from,
      to: addOneDay(props.to),
    };
  } else if (props.dateRangeType && props.dateRangeType !== 'custom') {
    const dateRange = rangeTypes.find(rt => rt.id === props.dateRangeType).dateRange;
    return {
      open: false,
      rangeType: props.dateRangeType,
      from: dateRange.from,
      to: dateRange.to,
    };
  } else {
    const dateRange = rangeTypes.find(rt => rt.id === defaultDateRangeType).dateRange;
    return {
      open: false,
      rangeType: defaultDateRangeType,
      from: dateRange.from,
      to: dateRange.to,
    };
  }
}

const styles = {
  wrapper: {
    display: 'inline-block',
  },
  dropDownWrapper: {
    float: 'left',
    marginRight: 24,
  },
  buttonWrapper: {
    float: 'left',
    marginTop: 6,
  },
  rangeTypeSelectField: {
    verticalAlign: 'middle',
    width: 170,
    icon: {
      fill: grey400,
    },
    underline: {
      borderColor: grey400,
    },
  },
  dateRangeButton: {
    width: 250,
    marginRight: 0,
  },
  dateRangeButtonLabel: {
    fontWeight: '500',
  },
  dateRangeButtonLabelArrow: {
    padding: '0 10px 3px',
    display: 'inline-block',
    verticalAlign: 'middle',
    height: 20,
    width: 20,
  },
};

@withQueryStringProps(['dateRangeType', 'from', 'to'])
@injectIntl
@connect()
class DateRangePicker extends Component {
  constructor(props) {
    super(props);
    this.state = getStateFromProps(props);
  }

  handleRangeTypeChange = (event, index, value) => {
    if (value === 'custom') {
      this.setState({
        rangeType: value,
        open: true,
        anchorEl: ReactDOM.findDOMNode(this.dateRangeButton),
      });
    } else {
      const rangeTypes = getRangeTypes(this.props.intl, this.props.firstDate);
      const dateRange = rangeTypes.find(rt => rt.id === value).dateRange;
      this.setState({
        rangeType: value,
        from: dateRange.from,
        to: dateRange.to,
      });
      this.props.qs.set({
        dateRangeType: null,
        from: dateRange.from,
        to: subtractOneDay(dateRange.to),
      });
    }
  };

  showDayPicker = (event) => {
    this.setState({
      open: true,
      anchorEl: event.currentTarget,
    });
  };

  hideDayPicker = () => {
    this.setState({
      open: false,
      anchorEl: null,
    });
  };

  handleDayPickerChange = ({from, to}) => {
    this.setState({
      from,
      to,
      open: false,
      rangeType: 'custom',
    });
    this.props.qs.set({
      dateRangeType: null,
      from,
      to: subtractOneDay(to),
    });
  }

  componentDidMount() {
    this.props.dispatch(setReportDateRangeFilter(
      this.props.report.id,
      {
        from: this.state.from,
        to: this.state.to,
      }
    ));
    if (this.props.dateRangeType) {
      this.props.qs.set({
        dateRangeType: null,
        from: this.state.from,
        to: subtractOneDay(this.state.to),
      }, true);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.props.from !== nextProps.from ||
      this.props.to !== nextProps.to ||
      this.props.firstDate !== nextProps.firstDate
    ) {
      this.setState(getStateFromProps(nextProps));
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      (prevState.from !== this.state.from || prevState.to !== this.state.to) &&
      (this.state.from && this.state.to)
    ) {
      this.props.dispatch(setReportDateRangeFilter(
        this.props.report.id,
        {
          from: this.state.from,
          to: this.state.to,
        }
      ));
    }
  }

  render() {
    return (
      <div style={styles.wrapper}>
        <HideIfPrint>
          <div>
            <div style={styles.dropDownWrapper}>
              <SelectField 
                value={this.state.rangeType}
                onChange={this.handleRangeTypeChange}
                style={styles.rangeTypeSelectField}
                iconStyle={styles.rangeTypeSelectField.icon}
                underlineStyle={styles.rangeTypeSelectField.underline}
              >
                {getRangeTypes(this.props.intl, this.props.firstDate).map(rangeType =>
                  <MenuItem
                    key={rangeType.id}
                    value={rangeType.id}
                    primaryText={rangeType.label}
                  />
                )}
              </SelectField>
            </div>
            <div style={styles.buttonWrapper}>
              <RaisedButton
                onTouchTap={this.showDayPicker}
                ref={el => this.dateRangeButton = el}
                style={styles.dateRangeButton}
              >
                <span style={styles.dateRangeButtonLabel}>
                  <span>
                    {moment(this.state.from).diff(moment(), 'days') === 0 ? 
                      <FormattedMessage id="today" />
                      :
                      moment.utc(this.state.from).format('D MMM YYYY')
                    }
                  </span>
                  <NavigationArrowForward
                    style={styles.dateRangeButtonLabelArrow}
                    color={grey500}
                  />
                  <span>
                    {moment(this.state.to).subtract(1, 'days').diff(moment(), 'days') === 0 ? 
                      <FormattedMessage id="today" />
                      :
                      moment.utc(this.state.to).subtract(1, 'days').format('D MMM YYYY')
                    }
                  </span>
                </span>
              </RaisedButton>
              <Popover
                open={this.state.open}
                anchorEl={this.state.anchorEl}
                anchorOrigin={{horizontal: 'right', vertical: 'top'}}
                targetOrigin={{horizontal: 'right', vertical: 'top'}}
                onRequestClose={this.hideDayPicker}
              >
                <DayPickerContainer
                  from={this.state.from}
                  to={this.state.to}
                  onChange={this.handleDayPickerChange}
                  firstDate={this.props.firstDate}
                />
              </Popover>
            </div>
          </div>
        </HideIfPrint>
        <ShowIfPrint>
          <span>
            {moment.utc(this.state.from).format('D MMM YYYY')}
            <NavigationArrowForward
              style={styles.dateRangeButtonLabelArrow}
              color={grey500}
            />
            {moment.utc(this.state.to).subtract(1, 'days').format('D MMM YYYY')}
          </span>
        </ShowIfPrint>
      </div>
    );
  }
}

export default createFragmentContainer(
  DateRangePicker,
  graphql`
    fragment DateRangePicker_report on Report {
      id
    }
  `,
);



// WEBPACK FOOTER //
// ./src/scenes/Report/components/ReportWrapper/components/ReportHeader/components/ReportFilters/components/DateRangePicker/DateRangePicker.js